import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { supabase } from '../services/supabase';
import { useAppContext } from '../context/AppContext';
import { useEmbeddedPage } from '../hooks/useEmbeddedPage';

const HeadlinesPage = () => {
  const [headlines, setHeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterScore, setFilterScore] = useState('all');
  const { darkMode } = useAppContext();
  const { handleBack } = useEmbeddedPage();
  const itemsPerPage = 20;

  useEffect(() => {
    fetchHeadlines();
  }, [filterScore]);

  const fetchHeadlines = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('rss_headlines')
        .select('*')
        .order('pub_date', { ascending: false });

      // Apply impact score filter
      if (filterScore === 'high') {
        query = query.gte('impact_score', 80);
      } else if (filterScore === 'moderate') {
        query = query.gte('impact_score', 40).lt('impact_score', 80);
      } else if (filterScore === 'general') {
        query = query.lt('impact_score', 40);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      setHeadlines(data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error fetching headlines:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const decodeHTMLEntities = (text) => {
    if (!text) return text;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  const renderHTMLContent = (html) => {
    if (!html) return '';
    return decodeHTMLEntities(html);
  };

  const renderHTMLAsJSX = (html) => {
    if (!html) return '';
    const decoded = decodeHTMLEntities(html);
    return <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decoded) }} />;
  };

  const extractSource = (link) => {
    if (!link) return '';
    try {
      if (link.includes('google.com/url') && link.includes('&url=')) {
        const urlMatch = link.match(/[&?]url=([^&]+)/);
        if (urlMatch) {
          const decodedUrl = decodeURIComponent(urlMatch[1]);
          const actualUrl = new URL(decodedUrl);
          const hostname = actualUrl.hostname.replace('www.', '');
          return hostname.split('.')[0].split(/[-_]/).map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        }
      }
      const url = new URL(link);
      const hostname = url.hostname.replace('www.', '');
      return hostname.split('.')[0].split(/[-_]/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    } catch {
      return '';
    }
  };

  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    const plainText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (plainText.length <= maxLength) return plainText;
    const truncated = plainText.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return truncated.slice(0, lastSpace > 0 ? lastSpace : maxLength) + '...';
  };

  const getDisplayText = (headline) => {
    return headline.content || headline.summary || '';
  };

  const getImpactCategory = (score) => {
    if (score === null || score === undefined) return null;
    if (score >= 80) return { label: 'High Impact', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' };
    if (score >= 40) return { label: 'Moderate', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' };
    return { label: 'General Interest', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' };
  };

  // Pagination
  const totalPages = Math.ceil(headlines.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedHeadlines = headlines.slice(startIdx, startIdx + itemsPerPage);

  // Generate pagination buttons (max ~10 visible at a time)
  const getPaginationButtons = () => {
    const maxButtons = 10;
    const pages = [];
    const halfWindow = Math.floor(maxButtons / 2);
    
    let startPage = Math.max(1, currentPage - halfWindow);
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);
    
    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    
    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push(null); // null represents ellipsis
      }
    }
    
    // Add range of pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(null); // null represents ellipsis
      }
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 dark:from-gray-900 dark:via-gray-800 dark:to-green-900">
      {/* SVG Banner Header */}
      <div className="relative w-full rounded-2xl overflow-hidden shadow-lg">
        <svg
          viewBox="0 0 1200 220"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-48 md:h-56 lg:h-64"
          role="img"
          aria-labelledby="banner-title"
          preserveAspectRatio="xMidYMid slice"
          style={{ display: "block" }}
        >
          <title id="banner-title">Headlines Banner</title>
          <defs>
            <linearGradient id="journalSkyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={darkMode ? "#1a3a2a" : "#d4e8d0"} />
              <stop offset="60%" stopColor={darkMode ? "#2a4a3a" : "#c8dfc4"} />
              <stop offset="100%" stopColor={darkMode ? "#3a5a4a" : "#a8c9a4"} />
            </linearGradient>
            <linearGradient id="journalSunGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={darkMode ? "#c9e6bf" : "#f0f5e8"} />
              <stop offset="100%" stopColor={darkMode ? "#a8d4a0" : "#d9e8d0"} />
            </linearGradient>
            <radialGradient id="journalSunRays" cx="50%" cy="50%" r="50%">
              <stop offset="60%" stopColor={darkMode ? "#c9e6bf" : "#f0f5e8"} stopOpacity={darkMode ? "0.12" : "0.3"} />
              <stop offset="100%" stopColor={darkMode ? "#c9e6bf" : "#f0f5e8"} stopOpacity="0" />
            </radialGradient>
          </defs>

          <rect width="1200" height="220" fill="url(#journalSkyGrad)" />

          {darkMode && (
            <g>
              {[...Array(40)].map((_, i) => (
                <circle
                  key={i}
                  cx={Math.random() * 1200}
                  cy={Math.random() * 120}
                  r={Math.random() * 1.5 + 0.5}
                  fill="#fff"
                  opacity={Math.random() * 0.5 + 0.5}
                />
              ))}
            </g>
          )}

          {/* Yosemite-style granite peaks */}
          <path
            d="M0 160 L80 110 L150 130 L220 100 L320 125 L420 95 L500 115 L600 85 L700 110 L800 90 L900 120 L1000 100 L1100 125 L1200 110 V220 H0Z"
            fill={darkMode ? "#2d5a3d" : "#7a9d6f"}
            opacity="0.8"
          />
          <path
            d="M0 175 L60 140 L140 160 L240 135 L360 155 L480 140 L550 165 L650 145 L750 170 L850 150 L950 170 L1050 155 L1150 175 L1200 165 V220 H0Z"
            fill={darkMode ? "#4a7a5a" : "#5a8b5f"}
            opacity="0.9"
          />

          <circle cx="1080" cy="45" r="38" fill="url(#journalSunGrad)" />
          <circle cx="1080" cy="45" r="80" fill="url(#journalSunRays)">
            <animate attributeName="r" values="70;90;70" dur="6s" repeatCount="indefinite" />
            <animate
              attributeName="opacity"
              values={darkMode ? "0.12;0.22;0.12" : "0.3;0.5;0.3"}
              dur="6s"
              repeatCount="indefinite"
            />
          </circle>

          <text
            x="600"
            y="145"
            textAnchor="middle"
            fontFamily="Times New Roman, Times, serif"
            fontWeight="bold"
            fontSize="48"
            fill={darkMode ? "#e6e7ef" : "#111111"}
            letterSpacing="0.12em"
            style={{
              textTransform: 'uppercase',
              filter: darkMode
                ? 'drop-shadow(2px 4px 0 #000) drop-shadow(0 2px 8px #0008)'
                : 'drop-shadow(2px 4px 0 #fff) drop-shadow(0 2px 8px #0001)',
            }}
          >
            The Journal
          </text>
        </svg>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold transition-colors"
          >
            ← Back
          </button>
        </div>

        {/* Filter Section */}
        <div className="mb-6 flex gap-4 items-center">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Filter by Impact:
          </label>
          <select
            value={filterScore}
            onChange={(e) => setFilterScore(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Headlines</option>
            <option value="high">High Impact (80+)</option>
            <option value="moderate">Moderate (40-79)</option>
            <option value="general">General Interest (0-39)</option>
          </select>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {headlines.length} headline{headlines.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-4 text-gray-600 dark:text-gray-400">Loading headlines...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-red-600 dark:text-red-400 py-4">
            Error loading headlines: {error}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && headlines.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No headlines found.</p>
          </div>
        )}

        {/* Headlines Grid */}
        {!loading && !error && paginatedHeadlines.length > 0 && (
          <>
            <div className="space-y-4">
              {paginatedHeadlines.map((headline) => (
                <div key={headline.id} className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-lg transition-shadow p-4">
                  <a
                    href={headline.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block group"
                  >
                    {/* Title and Badge */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight flex-1">
                        {renderHTMLAsJSX(headline.title)}
                      </h3>
                      {headline.impact_score !== null && headline.impact_score !== undefined && (
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${getImpactCategory(headline.impact_score).color}`}>
                          {getImpactCategory(headline.impact_score).label}
                        </span>
                      )}
                    </div>

                    {/* Source and Date */}
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        {extractSource(headline.link)}
                      </span>
                      {headline.pub_date && (
                        <>
                          <span>•</span>
                          <span>{new Date(headline.pub_date).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>

                    {/* Summary */}
                    {getDisplayText(headline) && (
                      <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                        {renderHTMLAsJSX(truncateText(getDisplayText(headline)))}
                      </div>
                    )}
                  </a>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  {getPaginationButtons().map((page, index) => 
                    page === null ? (
                      <span key={`ellipsis-${index}`} className="px-2 py-2 text-gray-600 dark:text-gray-400">
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 rounded-lg ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HeadlinesPage;
